import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { BrandLogo } from "@/components/BrandLogo";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function ClientMagicLinkLogin() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: "https://app.bodycodez.com/my-consultation",
        },
      });

      if (error) throw error;

      setLinkSent(true);
      toast({
        title: "Login link sent!",
        description: "Check your email for a magic link to sign in.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send login link",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <BrandLogo variant="light" className="h-10 mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">Your fitness journey, decoded.</p>
        </div>

        <Card className="border-border/50">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl">Access Your Consultation</CardTitle>
          </CardHeader>
          <CardContent>
            <AnimatePresence mode="wait">
              {linkSent ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-6"
                >
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Check your email!</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    We sent a login link to <span className="font-medium text-foreground">{email}</span>
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Click the link in your email to access your dashboard instantly. No password needed.
                  </p>
                  <Button
                    variant="ghost"
                    className="mt-6"
                    onClick={() => setLinkSent(false)}
                  >
                    Use a different email
                  </Button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onSubmit={handleSendLink}
                  className="space-y-4"
                >
                  <p className="text-muted-foreground text-sm text-center mb-4">
                    Enter your email to receive an instant login link.
                  </p>
                  
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      disabled={loading}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Login Link"
                    )}
                  </Button>

                  <p className="text-center text-xs text-muted-foreground mt-4">
                    No password required. We'll email you a secure link.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}